import { sanitizeApiErrorMessageForDisplay } from '@/utils/errorHelpers';

export interface SocialImportErrorBody {
  status?: string;
  code?: string;
  message?: string;
  error?: string;
  retryable?: boolean;
}

/** Thrown when `/api/v1/public/social/import` returns a non-OK HTTP status. */
export class SocialImportRequestError extends Error {
  readonly httpStatus: number;
  readonly failureCode: string | null;
  readonly retryable: boolean | null;

  constructor(
    message: string,
    options: {
      httpStatus: number;
      failureCode?: string | null;
      retryable?: boolean | null;
    },
  ) {
    super(message);
    this.name = 'SocialImportRequestError';
    this.httpStatus = options.httpStatus;
    this.failureCode = options.failureCode ?? null;
    this.retryable = options.retryable ?? null;
  }
}

export interface SocialImportScrapeFailureAnalytics {
  reason: string;
  failure_code?: string | null;
  http_status?: number | null;
  retryable?: boolean | null;
}

/** Stable Mixpanel props for `Inspo Import Scrape Failed` (prefer `failure_code` over long UI copy). */
export function getSocialImportScrapeFailureAnalytics(
  error: unknown,
): SocialImportScrapeFailureAnalytics {
  if (error instanceof SocialImportRequestError) {
    return {
      reason: error.failureCode ?? 'import_http_error',
      failure_code: error.failureCode,
      http_status: error.httpStatus,
      retryable: error.retryable,
    };
  }
  if (error instanceof Error) {
    return { reason: error.message.slice(0, 200) || 'unknown' };
  }
  return { reason: typeof error === 'string' ? error.slice(0, 200) : 'unknown' };
}

const ENRICHMENT_FAILED_USER_MESSAGE =
  "We couldn't pull place details for this video right now. Try again in a few minutes, or use a post with places listed in the caption.";

/**
 * Maps `/api/v1/public/social/import` error JSON + HTTP metadata to copy safe for Inspo import UI.
 */
export function userFacingSocialImportErrorMessage(
  httpStatus: number,
  statusText: string,
  body: SocialImportErrorBody | null,
): string {
  const code = typeof body?.code === 'string' ? body.code.trim() : '';
  if (code === 'ENRICHMENT_FAILED') {
    return ENRICHMENT_FAILED_USER_MESSAGE;
  }

  const fromBody =
    (typeof body?.error === 'string' && body.error.trim())
    || (typeof body?.message === 'string' && body.message.trim())
    || '';

  if (fromBody) {
    return sanitizeApiErrorMessageForDisplay(fromBody);
  }

  if (httpStatus === 400) {
    return 'Invalid URL';
  }

  const statusLabel = statusText.trim() || `HTTP ${httpStatus}`;
  return `Failed to import this link (${statusLabel}). Please try again.`;
}

export async function readSocialImportErrorBody(response: Response): Promise<SocialImportErrorBody | null> {
  try {
    const data: unknown = await response.json();
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      return data as SocialImportErrorBody;
    }
  } catch {
    // Non-JSON error bodies are ignored; caller falls back to status text.
  }
  return null;
}

export function socialImportRequestErrorFromResponse(
  response: Response,
  errorBody: SocialImportErrorBody | null,
  userMessage: string,
): SocialImportRequestError {
  const failureCode =
    typeof errorBody?.code === 'string' && errorBody.code.trim()
      ? errorBody.code.trim()
      : null;
  const retryable = typeof errorBody?.retryable === 'boolean' ? errorBody.retryable : null;

  return new SocialImportRequestError(userMessage, {
    httpStatus: response.status,
    failureCode,
    retryable,
  });
}
